import $ from 'jquery';
import ChartJS from 'chart.js';
import watermarkPlugin from '../helpers/Watermark';

const fontFamily = 'neue-haas-grotesk-text';
const colorBlack = '#1e1e22';
const colorWhite = '#FFFFFF';
const colorDarkGrey = '#4b5157';
const colorMediumGrey = '#96a1ad';
const colorLightGrey = '#e1e4e6';

class Chart {
    constructor() {
        this.initialize();
    }

    initialize() {
        this.chart = null;
        this.chartCanvas = document.getElementById('chart');
        this.downloadButton = document.getElementById('chart-download');
        this.chartConfig = window.chartConfig;
        this.createChart();
    }

    buildLegendConfig() {
        const { legend } = this.chartConfig;
        const display = legend.display === '1';
        const position = legend.position;

        return {
            display,
            position,
            fullWidth: true,
            fontSize: 12,
            fontStyle: 'normal',
            fontColor: colorDarkGrey,
            fontFamily,
            padding: 10,
            labels: {
                boxWidth: 13,
                fontSize: 14,
                fontColor: colorDarkGrey,
                padding: 10,
            },
        };
    }

    buildTitleConfig() {
        const { title } = this.chartConfig;
        const display = title.display === '1';
        const position = title.position;
        const text = title.text;

        return {
            display,
            position,
            fontSize: 24,
            fontFamily,
            fontColor: colorBlack,
            padding: 30,
            text,
        };
    }

    buildTooltipConfig() {
        const { tooltip } = this.chartConfig;
        const enabled = tooltip.enabled === '1';
        const displayColors = tooltip.displayColors === '1';

        return {
            enabled,
            mode: 'nearest',
            backgroundColor: colorBlack,
            titleFontFamily: fontFamily,
            titleFontSize: 12,
            titleFontColor: colorWhite,
            titleSpacing: 2,
            titleMarginBottom: 6,
            bodyFontFamily: fontFamily,
            bodyFontSize: 12,
            bodyFontColor: colorWhite,
            bodySpacing: 2,
            xPadding: 20,
            yPadding: 20,
            caretPadding: 2,
            caretSize: 5,
            cornerRadius: 0,
            displayColors,
        };
    }

    buildXAxesConfig() {
        const { xAxis } = this.chartConfig.axes;
        const displayScaleLabel = xAxis.scale.display === '1';
        const labelString = xAxis.scale.label;
        const displayGridLines = xAxis.gridLines.display === '1';
        const offsetGridLines = xAxis.gridLines.offsetGridLines === '1';
        const beginAtZero = xAxis.ticks.beginAtZero === '1';

        return [
            {
                scaleLabel: {
                    display: displayScaleLabel,
                    labelString,
                    lineHeight: 1.2,
                    fontColor: colorBlack,
                    fontFamily,
                    fontSize: 14,
                    padding: 10,
                },
                gridLines: {
                    display: displayGridLines,
                    color: colorLightGrey,
                    lineWidth: 1,
                    tickMarkLength: 10,
                    zeroLineWidth: 1,
                    zeroLineColor: colorMediumGrey,
                    offsetGridLines,
                },
                ticks: {
                    beginAtZero,
                    fontColor: colorDarkGrey,
                },
            },
        ];
    }

    buildYAxesConfig() {
        const { type, datasets } = this.chartConfig;
        const { yAxis } = this.chartConfig.axes;
        const displayScaleLabel = yAxis.scale.display === '1';
        const labelString = yAxis.scale.label;
        const displayGridLines = yAxis.gridLines.display === '1';
        const offsetGridLines = yAxis.gridLines.offsetGridLines === '1';
        const beginAtZero = yAxis.ticks.beginAtZero === '1';

        // set max tick value to be one more than it actually is
        // to avoid the top point being cut off
        let suggestedMax = null;
        datasets.forEach(dataset => {
            const data =
                type === 'scatter'
                    ? dataset.data.map(coord => parseFloat(coord.y))
                    : dataset.data.map(parseFloat);
            const maxValue = Math.max.apply(null, data);
            suggestedMax = maxValue > suggestedMax ? maxValue + 1 : suggestedMax;
        });

        return [
            {
                scaleLabel: {
                    display: displayScaleLabel,
                    labelString,
                    lineHeight: 1.2,
                    fontColor: colorBlack,
                    fontFamily,
                    fontSize: 14,
                    padding: 10,
                },
                gridLines: {
                    display: displayGridLines,
                    color: colorLightGrey,
                    lineWidth: 1,
                    tickMarkLength: 10,
                    zeroLineWidth: 1,
                    zeroLineColor: colorMediumGrey,
                    offsetGridLines,
                },
                ticks: {
                    beginAtZero,
                    fontColor: colorDarkGrey,
                    suggestedMax,
                },
            },
        ];
    }

    hexToRgba(hex) {
        const opacity = 0.3;
        let c;
        if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
            c = hex.substring(1).split('');
            if (c.length === 3) {
                c = [c[0], c[0], c[1], c[1], c[2], c[2]];
            }
            c = '0x' + c.join('');
            const rgb = [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',');
            return `rgba(${rgb},${opacity})`;
        }
        throw new Error('Bad Hex Value');
    }

    // Set fill color to be semi-transparent color of line
    setFillColor(datasets) {
        datasets.forEach(dataset => {
            if (dataset.fill) {
                dataset.backgroundColor = this.hexToRgba(dataset.backgroundColor);
            }
        });
        return datasets;
    }

    buildWatermark() {
        const image = new Image();
        image.src = '/static/dist/img/logo-on-light.png';

        return {
            image,
            x: 10,
            y: 10,
            width: 100,
            height: 29,
            alignX: 'right',
            alignY: 'top',
            opacity: 1,
        };
    }

    createChart() {
        const ctx = this.chartCanvas;
        const { type, labels } = this.chartConfig;
        const datasets = this.setFillColor(this.chartConfig.datasets);
        const legend = this.buildLegendConfig();
        const title = this.buildTitleConfig();
        const tooltips = this.buildTooltipConfig();
        const xAxes = this.buildXAxesConfig();
        const yAxes = this.buildYAxesConfig();
        const watermark = this.buildWatermark();

        // Add plugin to set chart background to be white
        ChartJS.plugins.register({
            beforeDraw: c => {
                let ctx = c.chart.ctx;
                ctx.fillStyle = colorWhite;
                ctx.fillRect(0, 0, c.chart.width, c.chart.height);
            },
        });

        // Add plugin for watermark
        ChartJS.plugins.register(watermarkPlugin);

        this.chart = new ChartJS(ctx, {
            type,
            data: {
                labels,
                datasets,
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                legend,
                title,
                tooltips,
                layout: {
                    padding: {
                        top: 10,
                        bottom: 50,
                        left: 0,
                        right: 0,
                    },
                },
                hover: {
                    mode: 'nearest',
                    intersect: true,
                },
                scales: {
                    xAxes,
                    yAxes,
                },
                bezierCurve: false,
                animation: {
                    onComplete: () => {
                        // http://www.chartjs.org/docs/latest/developers/api.html#tobase64image
                        const imgUrl = this.chart.toBase64Image();
                        this.downloadButton.href = imgUrl;
                    },
                },
                watermark,
            },
        });
    }

    static init() {
        if ($('.js-chart').length > 0) {
            new Chart();
        }
    }
}

export default Chart;
